import React, { useState, useEffect, Fragment, useCallback } from 'react';
import {
  Typography,
  Button,
  Switch,
  FormControlLabel,
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  Grow,
  CircularProgress,
  Box,
  Collapse,
} from '@material-ui/core';
import AssessmentOutlinedIcon from '@material-ui/icons/AssessmentOutlined';
import StorefrontOutlinedIcon from '@material-ui/icons/StorefrontOutlined';
import MonetizationOnOutlinedIcon from '@material-ui/icons/MonetizationOnOutlined';
import PeopleOutlinedIcon from '@material-ui/icons/PeopleOutlined';
import AssignmentReturnOutlinedIcon from '@material-ui/icons/AssignmentReturnOutlined';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import CloseIcon from '@material-ui/icons/Close';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import { useSnackbar } from '../utils/useSnackbar';
import * as api from '../api/index';
import cardStyles from '../components/Settings/SettingsCard.module.css';
import { CardSkeleton } from '../components/Skeletons';

/* ── tiny helpers ─────────────────────────────────────────── */

const StatusPill = ({ on, onLabel = 'Visible', offLabel = 'Hidden' }) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding: '4px 12px',
      borderRadius: 9999,
      fontWeight: 700,
      fontSize: '0.78rem',
      letterSpacing: '0.01em',
      backgroundColor: on ? '#f0fdf4' : '#fef2f2',
      border: `1px solid ${on ? '#86efac' : '#fca5a5'}`,
      color: on ? '#15803d' : '#b91c1c',
      boxShadow: on
        ? '0 1px 3px rgba(22, 163, 74, 0.12)'
        : '0 1px 3px rgba(220, 38, 38, 0.12)',
      transition: 'all 0.18s ease',
    }}
  >
    <span
      style={{
        width: 7,
        height: 7,
        borderRadius: '50%',
        backgroundColor: on ? '#16a34a' : '#dc2626',
        display: 'inline-block',
        boxShadow: on
          ? '0 0 0 2.5px rgba(34, 197, 94, 0.25)'
          : '0 0 0 2.5px rgba(239, 68, 68, 0.25)',
      }}
    />
    {on ? onLabel : offLabel}
  </span>
);

/* ── per-category card configuration ─────────────────────── */

const REPORT_CATEGORIES = [
  {
    key: 'sales',
    icon: AssessmentOutlinedIcon,
    iconVariant: 'cardIconBlue',
    title: 'Sales Reports',
    hint: 'Invoice analytics, payment breakdown, discounts',
    reports: [
      { key: 'sales', label: 'Sales' },
      { key: 'discount', label: 'Discount' },
      { key: 'itemSales', label: 'Item / Category Sales' },
      { key: 'paymentMode', label: 'Payment Mode' },
      { key: 'taxSummary', label: 'Tax Summary' },
    ],
  },
  {
    key: 'inventory',
    icon: StorefrontOutlinedIcon,
    iconVariant: 'cardIconTeal',
    title: 'Inventory & Purchasing Reports',
    hint: 'Stock adjustments, low stock alerts, purchases',
    reports: [
      { key: 'purchase', label: 'Purchase' },
      { key: 'stockAdjustment', label: 'Stock Adjustment' },
      { key: 'stockSummary', label: 'Stock Summary' },
      { key: 'lowStock', label: 'Low Stock' },
      { key: 'supplier-due-detail', label: 'Supplier Due (Detailed)' },
    ],
  },
  {
    key: 'finance',
    icon: MonetizationOnOutlinedIcon,
    iconVariant: 'cardIconRose',
    title: 'Finance Reports',
    hint: 'Profit & loss, customer ledger, statements',
    reports: [
      { key: 'profitLoss', label: 'Profit & Loss' },
      { key: 'customerLedger', label: 'Customer Ledger' },
      { key: 'customer-due-detail', label: 'Customer Due (Detailed)' },
      { key: 'statement', label: 'Statement' },
    ],
  },
  {
    key: 'staff',
    icon: PeopleOutlinedIcon,
    iconVariant: 'cardIconPurple',
    title: 'Staff Reports',
    hint: 'Staff performance, commission, shift summary',
    reports: [
      { key: 'commission', label: 'Commission' },
      { key: 'staffPerformance', label: 'Staff Performance' },
      { key: 'shiftSummary', label: 'Shift Summary' },
    ],
  },
  {
    key: 'goodsReturn',
    icon: AssignmentReturnOutlinedIcon,
    iconVariant: 'cardIconTeal',
    title: 'Goods Return Reports',
    hint: 'Returns history, pending approvals, refunds',
    reports: [
      { key: 'returnsList', label: 'Returns List' },
    ],
  },
];

/* ── main component ──────────────────────────────────────── */

const ReportVisibilitySettings = () => {
  const [settings, setSettings] = useState({});
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [expandedReports, setExpandedReports] = useState({});
  const [openSnackbar] = useSnackbar();

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.getGlobalSettings();
      setSettings(data.data.reportVisibility || {});
    } catch (error) {
      openSnackbar('Failed to load report visibility settings');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [openSnackbar]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const openEdit = (cardKey) => {
    setDraft(JSON.parse(JSON.stringify(settings)));
    setEditingCard(cardKey);
    setExpandedReports({ [cardKey]: true });
  };

  const closeEdit = () => {
    setEditingCard(null);
    setDraft(null);
  };

  const handleCategoryToggle = (categoryKey) => {
    setDraft((prev) => ({
      ...prev,
      [categoryKey]: {
        ...prev[categoryKey],
        enabled: !prev[categoryKey]?.enabled,
      },
    }));
  };

  const handleReportToggle = (categoryKey, reportKey) => {
    setDraft((prev) => ({
      ...prev,
      [categoryKey]: {
        ...prev[categoryKey],
        reports: {
          ...prev[categoryKey]?.reports,
          [reportKey]: !prev[categoryKey]?.reports?.[reportKey],
        },
      },
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.updateGlobalSettings({ reportVisibility: draft });
      setSettings(draft);
      openSnackbar('Report visibility settings updated successfully');
      closeEdit();
    } catch (error) {
      openSnackbar('Failed to update report visibility settings');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Fragment>
        {REPORT_CATEGORIES.map((c) => (
          <CardSkeleton key={c.key} />
        ))}
      </Fragment>
    );
  }

  return (
    <Fragment>
      {REPORT_CATEGORIES.map((card) => {
        const Icon = card.icon;
        const categoryData = settings[card.key] || { enabled: true, reports: {} };
        const isEnabled = categoryData.enabled !== false;
        const editing = editingCard === card.key;
        const draftData = draft?.[card.key] || categoryData;

        const allReportsHidden = card.reports.every(
          (r) => categoryData.reports?.[r.key] === false
        );

        return (
          <div
            className={`${cardStyles.card} ${!isEnabled || allReportsHidden ? cardStyles.cardDisabled : cardStyles.cardActive}`}
            key={card.key}
            style={{ opacity: !isEnabled ? 0.75 : 1 }}
          >
            <div className={cardStyles.cardHeader}>
              <span className={`${cardStyles.cardIcon} ${cardStyles[card.iconVariant]}`}>
                <Icon fontSize="small" />
              </span>
              <Typography className={cardStyles.cardTitle}>{card.title}</Typography>
            </div>

            <div className={cardStyles.cardBody}>
              <div className={cardStyles.statBlock}>
                <span className={cardStyles.statLabel}>Visibility</span>
                <div className={cardStyles.statValueRow}>
                  {!isEnabled ? (
                    <StatusPill on={false} onLabel="Category Visible" offLabel="Category Hidden" />
                  ) : allReportsHidden ? (
                    <StatusPill on={false} onLabel="All Reports" offLabel="All Hidden" />
                  ) : (
                    <StatusPill on={true} onLabel="Category Visible" offLabel="Category Hidden" />
                  )}
                </div>
                <span className={cardStyles.statHint}>{card.hint}</span>
              </div>

              <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #e0e0e0' }}>
                <Typography variant="caption" style={{ fontWeight: 600, color: '#334155', display: 'block', marginBottom: 8 }}>
                  Reports:
                </Typography>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {card.reports.map((report) => {
                    const isReportVisible = !isEnabled ? false : categoryData.reports?.[report.key] !== false;
                    return (
                      <div
                        key={report.key}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          fontSize: '0.85rem',
                          color: isReportVisible ? '#065f46' : '#991b1b',
                        }}
                      >
                        <span style={{ fontSize: '1.1rem' }}>{isReportVisible ? '✓' : '✕'}</span>
                        <span>{report.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className={cardStyles.cardActions}>
              <Button
                className={cardStyles.editButton}
                disableElevation
                onClick={() => openEdit(card.key)}
                startIcon={<EditOutlinedIcon style={{ fontSize: 15 }} />}
              >
                Edit
              </Button>
            </div>

            {/* ── edit dialog ── */}
            <Dialog
              open={editing}
              onClose={closeEdit}
              fullWidth
              maxWidth="sm"
              TransitionComponent={Grow}
              BackdropProps={{
                style: { backdropFilter: 'blur(3px)', backgroundColor: 'rgba(15,23,42,0.35)' },
              }}
              PaperProps={{
                style: {
                  borderRadius: 20,
                  overflow: 'hidden',
                  boxShadow: '0 25px 50px -12px rgba(15, 23, 42, 0.25)',
                },
              }}
            >
              <div className={cardStyles.dialogHeader}>
                <div className={cardStyles.dialogHeaderLeft}>
                  <Icon />
                  <Typography variant="h6" style={{ fontWeight: 700 }}>
                    {card.title}
                  </Typography>
                </div>
                <IconButton size="small" className={cardStyles.dialogCloseBtn} onClick={closeEdit}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </div>

              <DialogContent className={cardStyles.dialogBody}>
                <div style={{ marginBottom: 16 }}>
                  <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8 }}>
                    Category Status
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={draftData.enabled !== false}
                        onChange={() => handleCategoryToggle(card.key)}
                        color="primary"
                        disabled={saving}
                      />
                    }
                    label={`${draftData.enabled !== false ? 'Show' : 'Hide'} entire category`}
                  />
                </div>

                <Box style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #e0e0e0' }}>
                  <Box
                    onClick={() =>
                      setExpandedReports((prev) => ({
                        ...prev,
                        [card.key]: !prev[card.key],
                      }))
                    }
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      marginBottom: 8,
                    }}
                  >
                    {expandedReports[card.key] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    <Typography variant="subtitle2" style={{ fontWeight: 600, marginLeft: 4 }}>
                      Individual Reports
                    </Typography>
                  </Box>

                  <Collapse in={expandedReports[card.key]} timeout="auto">
                    <div style={{ paddingLeft: 24, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {card.reports.map((report) => (
                        <FormControlLabel
                          key={report.key}
                          control={
                            <Switch
                              checked={draftData.enabled !== false && draftData.reports?.[report.key] !== false}
                              onChange={() => handleReportToggle(card.key, report.key)}
                              color="primary"
                              disabled={saving || draftData.enabled === false}
                            />
                          }
                          label={report.label}
                          style={{ opacity: draftData.enabled === false ? 0.5 : 1 }}
                        />
                      ))}
                    </div>
                  </Collapse>
                </Box>
              </DialogContent>

              <DialogActions className={cardStyles.dialogActions}>
                <Button className={cardStyles.saveBtn} disableElevation onClick={handleSave} disabled={saving}>
                  {saving ? <CircularProgress size={24} /> : 'Save Settings'}
                </Button>
                <Button className={cardStyles.cancelBtn} disableElevation onClick={closeEdit} disabled={saving}>
                  Cancel
                </Button>
              </DialogActions>
            </Dialog>
          </div>
        );
      })}
    </Fragment>
  );
};

export default ReportVisibilitySettings;
